import { convexQuery } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Edit3, Plus, Receipt, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  stripeTaxManualSyncTaxCategoryRates,
  taxCategoriesGetAll,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import { convexClient } from "~/integrations/convex/provider";
import { useActiveOrganizationConvexId } from "~/integrations/vortex-auth/hooks";
import { ProductEvents } from "~/lib/posthog";

export function TaxCategoriesList() {
  const navigate = useNavigate();
  const { organizationId } = useActiveOrganizationConvexId();

  const { data: taxCategories, refetch } = useQuery(
    convexQuery(taxCategoriesGetAll, {})
  );

  // Check if any categories use automatic (Stripe) tax codes
  const hasAutomaticTaxCategories = taxCategories?.some((cat) => cat.taxCode);

  const syncTaxRatesMutation = useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error("No organization");
      }
      return convexClient.action(stripeTaxManualSyncTaxCategoryRates, {
        organizationId,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success(result.message);
        void refetch();
        ProductEvents.settingsUpdated("tax-rates-sync");
      } else {
        toast.error(result.message);
      }
    },
    onError: () => {
      toast.error("Failed to sync tax rates");
    },
  });

  // V4.1: Route-based navigation for delete modal
  const navigateToDelete = (id: string, name: string, value: number) => {
    void navigate({
      to: "/settings/tax-categories/$id/delete",
      params: { id },
      search: { name, value: value.toString() },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="h2 font-heading text-foreground text-balance">
            Tax Categories
          </h1>
          <p className="text-muted-foreground text-pretty">
            Manage your tax rates for items in your menus.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasAutomaticTaxCategories && (
            <Button
              disabled={syncTaxRatesMutation.isPending}
              onClick={() => syncTaxRatesMutation.mutate()}
              size="lg"
              variant="outline"
            >
              <RefreshCw
                className={`size-4 ${syncTaxRatesMutation.isPending ? "animate-spin" : ""}`}
              />
              {syncTaxRatesMutation.isPending
                ? "Syncing..."
                : "Refresh Tax Rates"}
            </Button>
          )}
          {/* V4.1: Route-based create for URL context tracking */}
          <Button
            onClick={() => navigate({ to: "/settings/tax-categories/new" })}
            size="lg"
          >
            <Plus className="size-4" />
            Add Tax Category
          </Button>
        </div>
      </div>

      <div
        className="border-muted-foreground/5 bg-background w-full max-w-4xl overflow-hidden rounded-md border shadow-xs"
        id="taxCategoriesList"
      >
        <div className="border-muted-foreground/5 bg-muted/30 grid h-11 grid-cols-4 gap-0 border-b px-6">
          <div className="col-span-1">
            <p className="label text-muted-foreground">Name</p>
          </div>
          <div className="col-span-1">
            <p className="label text-muted-foreground">Type</p>
          </div>
          <div className="col-span-1">
            <p className="label text-muted-foreground">Rate / Tax Code</p>
          </div>
          <div className="col-span-1" />
        </div>
        {taxCategories?.length === 0 && (
          <Empty className="py-12">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Receipt />
              </EmptyMedia>
              <EmptyTitle>No tax categories yet</EmptyTitle>
              <EmptyDescription>
                Create tax categories to apply tax rates to items in your menus.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
        {taxCategories?.map((item) => (
          <div
            className="border-muted-foreground/5 hover:bg-sidebar/50 grid h-11 grid-cols-4 gap-0 border-b px-6 transition-[color,box-shadow] last:border-b-0"
            key={item._id}
          >
            <div className="col-span-1 flex items-center" id="tax-name">
              <p className="text-foreground">{item.name}</p>
            </div>
            <div className="col-span-1 flex items-center" id="tax-type">
              {item.taxCode ? (
                <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                  Automatic
                </span>
              ) : (
                <span className="bg-muted text-muted-foreground inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium">
                  Manual
                </span>
              )}
            </div>
            <div className="col-span-1 flex items-center" id="tax-rate">
              {item.taxCode ? (
                <p
                  className="text-foreground truncate text-sm"
                  title={item.taxCode}
                >
                  {item.taxCode}
                </p>
              ) : (
                <p className="text-foreground">
                  {item.value.toLocaleString("en-US")}%
                </p>
              )}
            </div>
            <div className="col-span-1 flex items-center justify-end space-x-2">
              {item.name === "Untaxed" && item.value === 0 ? null : (
                <>
                  <button
                    className="bg-muted h-9 w-9 rounded-full p-2"
                    id="edit-button"
                    onClick={() =>
                      navigate({
                        to: `/settings/tax-categories/${item._id}/edit`,
                      })
                    }
                    type="button"
                  >
                    <Edit3 className="text-foreground size-5" />
                  </button>
                  <button
                    className="bg-destructive/10 h-9 w-9 rounded-full p-2"
                    id="trash-button"
                    onClick={() =>
                      navigateToDelete(item._id, item.name, item.value)
                    }
                    type="button"
                  >
                    <Trash2 className="text-destructive size-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
